import { eventIterator, oc } from "@orpc/contract";
import { z } from "zod";
import {
  answerInputSchema,
  archiveInputSchema,
  chatSnapshotSchema,
  chatSummarySchema,
  createChatInputSchema,
  keptViewSchema,
  listQuerySchema,
  messageViewSchema,
  queryInteger,
  queuedViewSchema,
  runViewSchema,
  searchQuerySchema,
  searchResultSchema,
  sendInputSchema,
  streamFrameSchema,
  visibilityInputSchema,
  wordingInputSchema,
  wordingViewSchema,
} from "./chat";
import { catalogErrors } from "./errors";
import { idSchema } from "./ids";

/**
 * The regional Chats API as one contract: the server implements it and the browser calls it, and
 * neither imports the other. Paths, methods and statuses are the HTTP surface; the server mounts
 * this under `/api`. What goes in and out is the vocabulary of `./chat`.
 *
 * Inputs are "detailed", as Connections' are, so path parameters, the query string and the body
 * stay apart. A malformed ID names something that cannot exist, so the server answers an issue
 * under `params` with NOT_FOUND and anything else with VALIDATION_ERROR. A chat that is private
 * to someone else is NOT_FOUND too, never FORBIDDEN. Every procedure can refuse with the shared
 * catalog codes below, and the few that have more say so on their own route.
 */
const workspace = z.object({ workspaceId: z.string().min(1) });
const inWorkspace = z.object({ params: workspace });
const onChat = workspace.extend({ chatId: idSchema("thread") });
const inChat = z.object({ params: onChat });
const onRun = z.object({ params: onChat.extend({ runId: idSchema("run") }) });
const onRequest = z.object({ params: onChat.extend({ requestId: idSchema("humanRequest") }) });
const onQueued = z.object({ params: onChat.extend({ inboxItemId: idSchema("inboxItem") }) });

const cursorQuery = queryInteger.pipe(z.int().nonnegative());

/** What a command that may change the run leaves behind: the run as it is now, or none. */
const runResultSchema = z.strictObject({ run: runViewSchema.nullable() });
/** An answer's result: the run it started, and where "Keep for next time" put it, when asked. */
const answerResultSchema = z.strictObject({
  run: runViewSchema.nullable(),
  kept: keptViewSchema.optional(),
});

/** `started` means the message began a run now; `queued` means it waits behind the one working. */
const sendResultSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("started"),
    message: messageViewSchema,
    runId: idSchema("run"),
  }),
  z.strictObject({
    kind: z.literal("queued"),
    queued: queuedViewSchema,
    runId: idSchema("run"),
  }),
]);

const route = oc.$route({ inputStructure: "detailed" });

/**
 * A message can be refused as too long. One that carries files can also refuse a file that has not
 * finished uploading, and more files than a message may hold. A file that cannot be used at all
 * (not found, not the sender's, failed) is answered as a validation error.
 */
const takesFiles = route.errors(
  catalogErrors(["MESSAGE_TOO_LARGE", "FILE_NOT_READY", "FILE_LIMIT"]),
);

export const chatContract = oc
  .errors(
    catalogErrors([
      "UNAUTHORIZED",
      "FORBIDDEN",
      "NOT_FOUND",
      "CONFLICT",
      "VALIDATION_ERROR",
      "INTERNAL_ERROR",
      "SERVICE_UNAVAILABLE",
    ]),
  )
  .tag("Chats")
  .prefix("/workspaces/{workspaceId}/chats")
  .router({
    // Static segments are declared first; the router prefers them over a chat ID either way, and
    // neither word is a valid chat ID.
    list: route
      .route({ method: "GET", path: "/" })
      .input(inWorkspace.extend({ query: listQuerySchema }))
      .output(z.strictObject({ chats: z.array(chatSummarySchema), next: z.string().nullable() })),
    recent: route
      .route({ method: "GET", path: "/recent" })
      .input(inWorkspace)
      .output(z.array(chatSummarySchema)),
    search: route
      .route({ method: "GET", path: "/search" })
      .input(inWorkspace.extend({ query: searchQuerySchema }))
      .output(z.array(searchResultSchema)),
    create: takesFiles
      .route({ method: "POST", path: "/", successStatus: 201 })
      .input(inWorkspace.extend({ body: createChatInputSchema }))
      .output(chatSnapshotSchema),
    open: route
      .route({ method: "GET", path: "/{chatId}" })
      .input(inChat)
      .output(chatSnapshotSchema),
    // `before` is the `seq` of the oldest message the caller already holds.
    earlier: route
      .route({ method: "GET", path: "/{chatId}/messages" })
      .input(inChat.extend({ query: z.object({ before: cursorQuery }) }))
      .output(z.strictObject({ messages: z.array(messageViewSchema), hasEarlier: z.boolean() })),
    send: takesFiles
      .route({ method: "POST", path: "/{chatId}/messages" })
      .input(inChat.extend({ body: sendInputSchema }))
      .output(sendResultSchema),
    withdraw: route
      .route({ method: "DELETE", path: "/{chatId}/queue/{inboxItemId}", successStatus: 204 })
      .input(onQueued),
    // Addressed by run so a late click cannot stop the run that followed.
    abort: route
      .errors(catalogErrors(["RUN_NOT_ACTIVE"]))
      .route({ method: "POST", path: "/{chatId}/runs/{runId}/abort" })
      .input(onRun)
      .output(runResultSchema),
    continue: route
      .route({ method: "POST", path: "/{chatId}/continue" })
      .input(inChat)
      .output(runResultSchema),
    answerRequest: route
      .errors(catalogErrors(["MESSAGE_TOO_LARGE", "REQUEST_NOT_PENDING"]))
      .route({ method: "POST", path: "/{chatId}/requests/{requestId}/answer" })
      .input(onRequest.extend({ body: answerInputSchema }))
      .output(answerResultSchema),
    /**
     * Abacor's wording of what to keep from an answer to a case step's question, before it is
     * sent: one sentence, or null when it has none. Only the person the question asks reads it.
     */
    wordAnswer: route
      .errors(catalogErrors(["REQUEST_NOT_PENDING"]))
      .route({ method: "POST", path: "/{chatId}/requests/{requestId}/wording" })
      .input(onRequest.extend({ body: wordingInputSchema }))
      .output(wordingViewSchema),
    cancelRequest: route
      .errors(catalogErrors(["REQUEST_NOT_PENDING"]))
      .route({ method: "POST", path: "/{chatId}/requests/{requestId}/cancel" })
      .input(onRequest)
      .output(runResultSchema),
    setVisibility: route
      .route({ method: "PUT", path: "/{chatId}/visibility" })
      .input(inChat.extend({ body: visibilityInputSchema }))
      .output(chatSummarySchema),
    // Only the owner archives. An archived chat can still be opened and read.
    setArchived: route
      .route({ method: "PUT", path: "/{chatId}/archived" })
      .input(inChat.extend({ body: archiveInputSchema }))
      .output(chatSummarySchema),
    remove: route.route({ method: "DELETE", path: "/{chatId}", successStatus: 204 }).input(inChat),
    // Server-sent events. `after` is the last `seq` the caller holds, and the server writes strict
    // frames. A client reads them with `parseStreamFrame` (`./chat`) rather than trusting the
    // typed items: it keeps the events it knows when a newer server sends one it does not.
    stream: route
      .route({ method: "GET", path: "/{chatId}/stream" })
      .input(inChat.extend({ query: z.object({ after: cursorQuery }) }))
      .output(eventIterator(streamFrameSchema)),
  });

export type ChatContract = typeof chatContract;
